import { LoyaltyCircleProgressVariant } from "components/atoms/LoyaltyCircleProgress";
import { LevelRankLoyalty, RankLoyalty } from "types/Loyalty";
import useTranslate from "hooks/useTranslate";

const getVariant = (
  level: LevelRankLoyalty,
  userExp: number,
  index: number,
  indexLast: number,
): LoyaltyCircleProgressVariant | undefined => {
  if (!level || userExp === undefined) {
    return undefined;
  }

  if (index === 0 || index === indexLast) {
    return "bordered";
  }

  if (userExp >= level.requirement) {
    return "colored";
  }

  return undefined;
};

type FormatLevelsParams = {
  t: ReturnType<typeof useTranslate>;
  rank?: RankLoyalty;
  nextRank?: RankLoyalty;
  userExperience?: number;
};

export const formatLevels = (
  params: FormatLevelsParams,
): Array<{
  variant: "colored" | "bordered" | undefined;
  name: string;
  required: number;
}> => {
  const { t, userExperience, rank, nextRank } = params;
  if (!rank || userExperience === undefined) {
    return [];
  }

  const levels: LevelRankLoyalty[] = nextRank
    ? [
        ...rank.levels.map((level, index) => ({
          ...level,
          name: `${t(rank.name)} ${index + 1}`,
        })),
        { ...nextRank.levels?.[0], name: `${t(nextRank.name)} 1` },
      ]
    : rank.levels.map((level, index) => ({
        ...level,
        name: `${t(rank.name)} ${index + 1}`,
      }));

  return levels.map((level, index, arr) => ({
    variant: getVariant(level, userExperience, index, arr.length - 1),
    required: level.requirement,
    name: level.name,
  }));
};

type CalcProgressParams = {
  userExperience: number;
  currentRank: RankLoyalty;
  nextRank?: RankLoyalty;
};

export const calcProgressPercent = (params: CalcProgressParams): number => {
  const { userExperience, currentRank, nextRank } = params;

  if (!userExperience || !currentRank) {
    return 0;
  }
  const start = currentRank?.levels?.[0].requirement;

  if (userExperience < start) {
    return 0;
  }

  const levels = nextRank?.levels?.[0]
    ? [...currentRank.levels, nextRank?.levels?.[0]]
    : currentRank.levels;
  const countParts = levels.length - 1;
  const percentPart = 100 / countParts;
  let result1 = 0;

  levels.forEach((currentLevel, index, levelsStart) => {
    const indexNext = index + 1;
    const nextLevelRequirement = levelsStart[indexNext]?.requirement;

    if (!nextLevelRequirement) {
      return;
    }

    if (userExperience >= nextLevelRequirement) {
      result1 += percentPart;
      return;
    }

    if (
      userExperience > currentLevel?.requirement &&
      userExperience < nextLevelRequirement
    ) {
      const userExp = userExperience - currentLevel?.requirement;
      const experienceLevel = nextLevelRequirement - currentLevel?.requirement;
      result1 += (userExp / experienceLevel) * percentPart;
    }
  });

  return result1 > 100 ? 100 : result1;
};
